import {
  ContractChangeFragment,
  ObjectiveStatus,
  ObjectiveDto,
  SalaryOfYearFragment,
  TargetBonusOfYearFragment,
  CurrencyConversionDto,
  Currency,
} from '../../../../../types'
import { calculateBonusWithSalary, ContractWithValidity, FlexibleBonusOverride } from '../../shared/targetBonusCalculation-util'
import { retrieveCurrencyConversionRate } from '../../../../../shared/currencyConversionUtil'
import { filterObjectivesByTypeStatusAndRefYear } from '../../../okr/util'

export const validatePastTargetsData = (
  employeeTimezone: string,
  isBonusValidationEnabled: boolean,
  objectivesDtoList: ObjectiveDto[],
  existingDevObjectivesToBeClosed: boolean,
  targetBonusOfYear: TargetBonusOfYearFragment[],
  salaryOfYear: SalaryOfYearFragment[],
  currentSalary: number,
  currentBonus: number,
  referenceYear: number,
  contractChanges: ContractChangeFragment[] | undefined = [],
  currencyConversionData: CurrencyConversionDto[],
  currentContractCurrency: Currency,
  startDate: string,
  endDate?: string,
  contractsInYear: ContractWithValidity[] = [],
  flexibleBonusOverride?: FlexibleBonusOverride,
  flexibleBonusUnknownCurrency?: Currency,
): {
  cannotProceed: boolean
  isMonetaryTargetMissing: boolean
  tooMuchTargetBonusProvided: boolean
  allPastMonetaryObjectivesChecked: boolean
  existingDevObjectivesToBeClosed: boolean
  atLeastOnePastMonetaryTargetWithWrongCurrency: boolean
  unknownCurrenciesForConversion: Currency[]
  existingOutdatedTargets: boolean
} => {
  // Closed targets are included to consider the already paid achieved amount
  const monetaryObjectivesToBeChecked = filterObjectivesByTypeStatusAndRefYear(
    objectivesDtoList,
    true,
    [ObjectiveStatus.Approved, ObjectiveStatus.PastApproved, ObjectiveStatus.Closed],
    referenceYear,
  )

  const unknownCurrenciesForConversion: Currency[] = [
    ...new Set(
      [
        ...monetaryObjectivesToBeChecked
          .filter((obj): boolean => {
            if (obj.currency! !== currentContractCurrency) {
              const conversionRate: number | undefined = retrieveCurrencyConversionRate(
                obj.currency!,
                currentContractCurrency,
                currencyConversionData,
              )
              if (!conversionRate) {
                return true
              }
            }
            return false
          })
          .map((obj) => obj.currency!),
        ...(flexibleBonusUnknownCurrency ? [flexibleBonusUnknownCurrency] : []),
      ].filter((currency): currency is Currency => !!currency),
    ),
  ]

  const allPastMonetaryObjectivesChecked =
    monetaryObjectivesToBeChecked.filter((x) => (x.achievedAmount || x.achievedAmount === 0) && x.achievedAmount >= 0).length ===
    monetaryObjectivesToBeChecked.length

  const rawSumOfMonetaryTargets = monetaryObjectivesToBeChecked.reduce((sum, obj) => {
    if (obj.targetAmount) {
      if (obj.currency !== currentContractCurrency) {
        const currencyConversionRate: number | undefined = retrieveCurrencyConversionRate(
          obj.currency!,
          currentContractCurrency,
          currencyConversionData,
        )
        if (currencyConversionRate) {
          return sum + Number((obj.targetAmount * currencyConversionRate).toFixed(2))
        } else {
          return sum + obj.targetAmount
        }
      }
      return sum + obj.targetAmount
    }
    return sum
  }, 0)

  const roundedSumOfMonetaryTargets = Number(rawSumOfMonetaryTargets.toFixed(2))
  // fix -0 case
  const sumOfMonetaryTargets = roundedSumOfMonetaryTargets !== -0 ? roundedSumOfMonetaryTargets : 0

  const targetBonusCalc = calculateBonusWithSalary(
    employeeTimezone,
    isBonusValidationEnabled,
    referenceYear,
    contractChanges,
    targetBonusOfYear,
    salaryOfYear,
    currentSalary,
    currentBonus,
    currencyConversionData,
    currentContractCurrency,
    startDate,
    endDate,
    contractsInYear,
    flexibleBonusOverride,
  )
  const sumOfTargetBonus = targetBonusCalc.flexibleBonusAmount ?? targetBonusCalc.amount

  let tooMuchTargetBonusProvided = sumOfTargetBonus - sumOfMonetaryTargets < 0

  const isMonetaryTargetMissing = sumOfTargetBonus > sumOfMonetaryTargets

  const atLeastOnePastMonetaryTargetWithWrongCurrency = monetaryObjectivesToBeChecked
    .filter((obj) => obj.status !== ObjectiveStatus.Closed)
    .some((obj) => obj.currency !== currentContractCurrency)

  if (tooMuchTargetBonusProvided) {
    // Edge case: if already tooMuchTargetBonus has been provided from closed objectives, continue anyway!
    const existingTargets = monetaryObjectivesToBeChecked.filter(
      (obj) =>
        (obj.status === ObjectiveStatus.Approved && obj.targetAmount) || (obj.status === ObjectiveStatus.PastApproved && obj.targetAmount),
    )
    if (existingTargets.length === 0) {
      tooMuchTargetBonusProvided = false
    }
  }

  // Edge case: target bonus is 0 but there was an objective with a target amount
  if (sumOfTargetBonus === 0 && sumOfMonetaryTargets > 0) {
    tooMuchTargetBonusProvided = false
  }

  // Edge case: Previous Past targets open due to missing previous AS meeting
  const existingOutdatedTargets =
    objectivesDtoList.filter(
      (objective) =>
        objective.appraisalSeason.year < referenceYear &&
        [ObjectiveStatus.Approved, ObjectiveStatus.PastApproved].some((status) => objective.status === status),
    ).length > 0

  const cannotProceed =
    existingOutdatedTargets ||
    !allPastMonetaryObjectivesChecked ||
    isMonetaryTargetMissing ||
    tooMuchTargetBonusProvided ||
    existingDevObjectivesToBeClosed ||
    atLeastOnePastMonetaryTargetWithWrongCurrency ||
    unknownCurrenciesForConversion.length > 0

  return {
    cannotProceed,
    isMonetaryTargetMissing,
    tooMuchTargetBonusProvided,
    allPastMonetaryObjectivesChecked,
    existingDevObjectivesToBeClosed,
    atLeastOnePastMonetaryTargetWithWrongCurrency,
    unknownCurrenciesForConversion,
    existingOutdatedTargets,
  }
}
